import DOMPurify from 'dompurify'

export const CONTENT_SCREEN_VERSION = 1

// Exactly what the server keeps; anything else it unwraps to its text, so showing more here would
// preview formatting respondents never see.
const ALLOWED_TAGS = ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'a']
const ALLOWED_ATTR = ['href', 'target', 'rel']
export const BODY_MAX_LENGTH = 10000
export const IMAGE_TEXT_MAX_LENGTH = 300
const SAFE_LINK = /^(?:https?:|mailto:)/i
const TITLE_MAX_LENGTH = 80

let purifier = null

// Own DOMPurify instance, so these hooks do not change sanitising elsewhere in the app.
const getPurifier = () => {
  if (!purifier) {
    purifier = DOMPurify(window)
    purifier.addHook('uponSanitizeAttribute', (node, data) => {
      if (data.attrName === 'href' && !SAFE_LINK.test(data.attrValue)) {
        data.keepAttr = false
      }
    })
    purifier.addHook('afterSanitizeAttributes', node => {
      if (node.tagName === 'A') {
        node.setAttribute('target', '_blank')
        node.setAttribute('rel', 'noopener noreferrer')
      }
    })
  }
  return purifier
}

// The rich-text editor writes every list as an <ol>, marking bullet items with
// data-list="bullet". The server keeps lists but not that attribute, so a bullet
// list would come back numbered: each run of bullet items becomes a real <ul>.
export const semanticLists = html => {
  if (!html || !html.includes('data-list')) return html || ''
  const doc = new DOMParser().parseFromString(html, 'text/html')
  doc.querySelectorAll('ol').forEach(list => {
    const runs = []
    ;[...list.children].forEach(item => {
      const tag = item.getAttribute('data-list') === 'bullet' ? 'ul' : 'ol'
      item.removeAttribute('data-list')
      item.querySelectorAll('.ql-ui').forEach(ui => ui.remove())
      const last = runs[runs.length - 1]
      if (last && last.tag === tag) last.items.push(item)
      else runs.push({tag, items: [item]})
    })
    runs.forEach(({tag, items}) => {
      const replacement = doc.createElement(tag)
      items.forEach(item => replacement.appendChild(item))
      list.parentNode.insertBefore(replacement, list)
    })
    list.remove()
  })
  return doc.body.innerHTML
}

export const sanitizeContentHtml = html =>
  getPurifier().sanitize(semanticLists(html), {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
  })

const blockTexts = html => {
  const doc = new DOMParser().parseFromString(
    sanitizeContentHtml(html),
    'text/html'
  )
  const blocks = [...doc.body.querySelectorAll('p, li')].map(el =>
    el.textContent.trim()
  )
  return blocks.length > 0 ? blocks : [doc.body.textContent.trim()]
}

export const contentBodyText = html =>
  blockTexts(html).filter(Boolean).join('\n')

export const emptyContentScreen = () => ({
  version: CONTENT_SCREEN_VERSION,
  headline: '',
  subheading: '',
  bodyHtml: '',
  image: null,
  imagePosition: 0,
})

export const validateContentScreen = content => {
  const errors = []
  const hasHeadline = !!content?.headline?.trim()
  const hasBody = contentBodyText(content?.bodyHtml) !== ''
  const hasImage = !!content?.image?.key

  if (!hasHeadline && !hasBody && !hasImage) {
    errors.push({
      code: 'EMPTY',
      message: 'Add a headline, some text or an image.',
    })
  }
  if (hasImage && !content.image.altText?.trim()) {
    errors.push({
      code: 'IMAGE_ALT_REQUIRED',
      message: 'Add alt text that describes the image.',
    })
  }
  if (hasBody && sanitizeContentHtml(content.bodyHtml).length > BODY_MAX_LENGTH) {
    errors.push({
      code: 'BODY_TOO_LONG',
      message: 'The text is too long - shorten it or split it across two screens.',
    })
  }
  return errors
}

export const normalizeContentScreen = content => ({
  version: CONTENT_SCREEN_VERSION,
  headline: content?.headline?.trim() || '',
  subheading: content?.subheading?.trim() || '',
  bodyHtml: contentBodyText(content?.bodyHtml)
    ? sanitizeContentHtml(content.bodyHtml)
    : '',
  image: content?.image?.key
    ? {
        key: content.image.key,
        altText: content.image.altText?.trim() || '',
        caption: content.image.caption?.trim() || '',
      }
    : null,
  imagePosition: content?.imagePosition === 1 ? 1 : 0,
})

export const contentScreenTitle = content => {
  const title =
    content?.headline?.trim() ||
    blockTexts(content?.bodyHtml).find(Boolean) ||
    content?.image?.altText?.trim() ||
    'Untitled content screen'
  return title.length > TITLE_MAX_LENGTH
    ? `${title.slice(0, TITLE_MAX_LENGTH - 1)}…`
    : title
}
